import { useEffect, useMemo, useState } from 'react'
import { BellOff, Trash2, Bell } from 'lucide-react'
import ProfileModalShell from '../../components/ProfileModalShell'
import { API_CONFIG } from '../../api/config'
import {
  apiGetUserNotifications,
  apiClearUserNotifications,
  getFormattedTodayGroup,
  getFormattedYesterdayGroup,
  type UserNotification as NotificationItem,
  type UserNotificationGroup as NotificationGroup,
} from '../../api/profile/userProfileApi'

export default function Notifications() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let mounted = true
    const loadNotifications = async () => {
      setLoading(true)
      try {
        const res = await apiGetUserNotifications()
        if (mounted && res.success && Array.isArray(res.data)) {
          setNotifications(res.data)
        }
      } catch (err) {
        console.error('Failed to load notifications', err)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    loadNotifications()
    return () => {
      mounted = false
    }
  }, [])

  const handleClearAll = async () => {
    try {
      await apiClearUserNotifications()
    } catch {}
    setNotifications([])
  }

  const todayGroup = useMemo(() => getFormattedTodayGroup(), [])
  const yesterdayGroup = useMemo(() => getFormattedYesterdayGroup(), [])

  // Normalize any legacy static date strings like 'Today–22 May 2026' into the dynamic date
  const normalizedNotifications = useMemo(() => {
    return notifications.map((n) => {
      if (n.group.startsWith('Today') || n.group === 'Today–22 May 2026') {
        return { ...n, group: todayGroup }
      }
      if (n.group === 'Yesterday' || n.group.startsWith('Yesterday')) {
        return { ...n, group: yesterdayGroup }
      }
      return n
    })
  }, [notifications, todayGroup, yesterdayGroup])

  const groupedNotifications = useMemo(() => {
    const defaultGroups: NotificationGroup[] = [todayGroup, yesterdayGroup]
    const allGroups = Array.from(new Set([...defaultGroups, ...normalizedNotifications.map((n) => n.group)]))
    return allGroups
      .map((group) => ({
        group,
        notifications: normalizedNotifications.filter(
          (notification) => notification.group === group,
        ),
      }))
      .filter(({ notifications: groupItems }) => groupItems.length > 0)
  }, [normalizedNotifications, todayGroup, yesterdayGroup])

  return (
    <ProfileModalShell>
      <section className="w-full">
        <div className="mb-[17px] flex items-center justify-between gap-4">
          <h1 className="font-host text-[18px] font-semibold tracking-[-0.035em] text-[#171717] sm:text-[20px]">
            Notifications
          </h1>

          {notifications.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="inline-flex h-[31px] items-center gap-1.5 rounded-[5px] bg-[#ffcaca] px-[13px] font-host text-[12px] font-medium text-[#151515] transition hover:bg-[#ffbcbc] active:scale-[0.98]"
            >
              <Trash2 className="h-[14px] w-[14px] stroke-[1.8]" />
              Clear All
            </button>
          )}
        </div>

        <div className="min-h-[490px] rounded-[11px] bg-white px-[clamp(16px,2vw,24px)] pb-[28px] pt-[17px] shadow-[0_1px_2px_rgba(0,0,0,0.015)]">
          {loading ? (
            <div className="flex min-h-[440px] flex-col items-center justify-center text-center">
              <p className="font-host text-[14px] text-[#777777]">Loading notifications...</p>
            </div>
          ) : groupedNotifications.length > 0 ? (
            <div>
              {groupedNotifications.map(({ group, notifications: items }, groupIndex) => (
                <section
                  key={group}
                  className={groupIndex === 0 ? '' : 'mt-[37px]'}
                >
                  <h2 className="font-host text-[13px] font-semibold text-[#171717] sm:text-[14px]">
                    {group}
                  </h2>

                  <div className="mt-[18px] space-y-[23px]">
                    {items.map((notification) => (
                      <NotificationRow
                        key={notification.id}
                        notification={notification}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="flex min-h-[440px] flex-col items-center justify-center text-center">
              <span className="grid h-14 w-14 place-items-center rounded-full border border-[#e1e1e1] bg-[#fafafa] text-[#555555]">
                <BellOff className="h-6 w-6 stroke-[1.6]" />
              </span>
              <h2 className="mt-4 font-host text-[16px] font-semibold text-[#202020]">
                No notifications
              </h2>
              <p className="mt-1.5 max-w-[280px] font-host text-[13px] leading-[1.5] text-[#777777]">
                You have cleared all notifications. New updates will appear here.
              </p>
            </div>
          )}
        </div>
      </section>
    </ProfileModalShell>
  )
}

type NotificationRowProps = {
  notification: NotificationItem
}

function NotificationRow({ notification }: NotificationRowProps) {
  return (
    <article className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-[11px] sm:grid-cols-[42px_minmax(0,1fr)_75px] sm:gap-[12px]">
      <span className="grid h-[38px] w-[38px] place-items-center rounded-full border border-[#dedede] bg-[#fafafa] sm:h-[40px] sm:w-[40px]">
        <Bell className="h-[20px] w-[20px] text-[#555555] stroke-[1.8]" />
      </span>

      <div className="min-w-0">
        <h3 className="font-host text-[12px] font-semibold leading-[1.25] text-[#161616] sm:text-[13px]">
          {notification.title}
        </h3>
        <p className="mt-[5px] max-w-[440px] font-host text-[11px] font-normal leading-[1.35] text-[#686868] sm:text-[12px]">
          {notification.description}
        </p>
      </div>

      <time className="self-center whitespace-nowrap text-right font-host text-[11px] font-medium text-[#161616] sm:text-[12px]">
        {notification.time}
      </time>
    </article>
  )
}
